<template>
  <el-dialog
    title="Change Room Status"
    :visible.sync="dialogVisible"
    width="500px"
    :before-close="handleClose"
  >
    <div v-if="room">
      <el-alert
        :title="`Room ${room.room_number} - ${room.room_type}`"
        type="info"
        :closable="false"
        show-icon
      >
        Current Status: <el-tag :type="getStatusType(room.status)">{{ room.status }}</el-tag>
      </el-alert>

      <el-form ref="form" :model="form" label-width="120px" class="mt-3">
        <el-form-item label="New Status" prop="status">
          <el-radio-group v-model="form.status">
            <el-radio-button label="available">Available</el-radio-button>
            <el-radio-button label="occupied">Occupied</el-radio-button>
            <el-radio-button label="reserved">Reserved</el-radio-button>
            <el-radio-button label="maintenance">Maintenance</el-radio-button>
          </el-radio-group>
        </el-form-item>

        <el-form-item label="Reason" v-if="form.status === 'maintenance'">
          <el-input
            v-model="form.reason"
            type="textarea"
            :rows="3"
            placeholder="Describe the maintenance required"
          ></el-input>
        </el-form-item>

        <el-form-item label="Notes">
          <el-input
            v-model="form.notes"
            type="textarea"
            :rows="2"
            placeholder="Additional notes (optional)"
          ></el-input>
        </el-form-item>
      </el-form>
    </div>

    <span slot="footer" class="dialog-footer">
      <el-button @click="handleClose">Cancel</el-button>
      <el-button type="primary" @click="handleSubmit" :loading="loading">
        Update Status
      </el-button>
    </span>
  </el-dialog>
</template>

<script>
import roomService from '@/services/room.service'

export default {
  name: 'RoomStatusDialog',
  props: {
    visible: Boolean,
    room: Object
  },
  data () {
    return {
      dialogVisible: false,
      loading: false,
      form: {
        status: '',
        reason: '',
        notes: ''
      }
    }
  },
  watch: {
    visible (val) {
      this.dialogVisible = val
      if (val && this.room) {
        this.form.status = this.room.status
      }
    },
    dialogVisible (val) {
      this.$emit('update:visible', val)
    }
  },
  methods: {
    getStatusType (status) {
      const types = {
        available: 'success',
        occupied: 'warning',
        reserved: 'primary',
        maintenance: 'danger'
      }
      return types[status] || 'info'
    },

    handleClose () {
      this.form = {
        status: '',
        reason: '',
        notes: ''
      }
      this.dialogVisible = false
    },

    async handleSubmit () {
      if (this.form.status === this.room.status) {
        this.$message.warning('Please select a different status')
        return
      }

      try {
        this.loading = true
        await roomService.updateRoomStatus(this.room.id, this.form.status)

        // If changing to maintenance, schedule maintenance
        if (this.form.status === 'maintenance' && this.form.reason) {
          await roomService.scheduleMaintenance(this.room.id, {
            type: 'Unscheduled Maintenance',
            description: this.form.reason,
            scheduled_date: new Date().toISOString()
          })
        }

        this.$message.success('Room status updated successfully')
        this.$emit('updated')
        this.handleClose()
      } catch (error) {
        this.$message.error('Failed to update room status')
      } finally {
        this.loading = false
      }
    },

    async saveStatus () {
      this.$refs.statusForm.validate(async (valid) => {
        if (valid) {
          try {
            await this.withLoading(async () => {
              await this.$roomService.updateRoomStatus(this.roomId, this.status)
              this.showSuccess(this.$t('room.status.message.updated'))
              this.$emit('status-updated')
              this.$refs.statusForm.resetFields()
              this.$emit('close', false)
            }, 'loading')
          } catch (error) {
            // Error is already handled by the global interceptor
            console.log('Component caught error in updateRoomStatus:', error)
          }
        }
      })
    }
  }
}
</script>

<style scoped>
.mt-3 {
  margin-top: 20px;
}
</style>
